import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import styles from './styles.scss';
import { useFormik } from 'formik';
import debounce from 'lodash.debounce';
import { Mention } from 'ui/elements/form/RichTextEditor';
import Button from 'ui/elements/buttons/Button';
import { mentionModule } from 'ui/elements/form/RichTextEditor/utils';
import MagicUrl from 'quill-magic-url';
import 'quill-mention/dist/quill.mention.css';
import 'quill-mention';
import 'styles/global/overrides/quill-editor-overrides.scss';
import 'ui/elements/form/RichTextEditor/components/BlockBlot';
import 'ui/elements/form/RichTextEditor/components/MentionBlot';
import useClickableMentions from 'ui/elements/form/RichTextEditor/useClickableMentions';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';
import PublicProfileDialog from 'domain/companies/ProfileDialog/ProfileDialog';
import { UpdateVisibility } from 'types/content';
import { CommunityContentTopic, TinyCommunity } from 'types/company/community';
import cx from 'classnames';

export type UpdateContext =
  | {
      type: 'room';
      roomId: string;
    }
  | {
      type: 'community';
      community: TinyCommunity;
      visibility: UpdateVisibility;
      topic?: CommunityContentTopic;
    }
  | {
      type: 'company';
      companyId: number;
      visibility: UpdateVisibility;
    }
  | {
      type: 'boardroom';
      companyId: number;
    };

interface Props {
  className?: string;
  id: string;
  onPost: (comment: { content: string }) => Promise<any>;
  onCancel?: () => void;
  editorRef?: RefObject<ReactQuill>;
  handleSearchForMentions?: (serchTerm: string) => Promise<Mention[]>;
  autoFocus: boolean;
  onInputClicked?: () => void;
  editorPlaceholder: string;
  size?: 'small' | 'large';
}

export default function CommentField(props: Props) {
  const size = props.size ?? 'large';
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setQuillCurrentSelection] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const fallbackRef = useRef<ReactQuill>(null);
  const quillRef = props.editorRef ?? fallbackRef;

  useEffect(() => {
    if (props.onInputClicked) quillRef?.current?.getEditingArea().addEventListener('click', props.onInputClicked);
    if (props.autoFocus) {
      quillRef?.current?.focus();
    }
  }, [quillRef]);

  const { userProfileIdToShow, setUserProfileIdToShow, companyProfileIdToShow, setCompanyProfileIdToShow } =
    useClickableMentions(containerRef, quillRef, [props.handleSearchForMentions, quillRef]);

  const formik = useFormik({
    initialValues: {
      comment: '',
    },
    onSubmit: (values, { resetForm }) => {
      if (values.comment.length === 0 || quillRef?.current?.editor?.getText().trim().length === 0) {
        return;
      }
      setIsSaving(true);
      props.onPost({ content: values.comment }).then(() => {
        setIsSaving(false);
        quillRef?.current?.editor?.deleteText(0, quillRef?.current?.editor?.getContents().length());
        resetForm();
      });
    },
  });

  const handleChangeFunc = (content: string, delta: any, source: any, editor: any) => {
    formik.setFieldValue('comment', editor.getHTML());
  };

  const handleMentionFunc = async function (
    searchTerm: string,
    renderList: (data: (Mention & { disabled?: boolean })[], searchTerm: string) => void,
  ) {
    const result = props.handleSearchForMentions
      ? await props.handleSearchForMentions(searchTerm)
      : await Promise.resolve([]);

    renderList(
      result.length !== 0 ? [{ id: 'header', value: 'header', type: 'user', disabled: true }, ...result] : [],
      searchTerm,
    );
  };

  const debouncedChangeFunc = debounce(handleChangeFunc, 200);
  const debouncedMentionFunc = debounce(handleMentionFunc, 200);

  const formats = ['link', 'styled-mention'];
  const isEmpty = formik.values.comment.length === 0 || quillRef?.current?.editor?.getText().trim().length === 0;

  const modules = useMemo(
    () => ({
      keyboard: {
        bindings: {
          // default behaviour is that tabbing inside editor will insert a tab character
          // disabling this will make the tab key change focus to the next element in the dom.
          tab: false,
          handleEnter: {
            key: 13,
            shiftKey: false,
            handler: function () {
              formik.submitForm();
              return false;
            },
          },
        },
      },
      magicUrl: true,
      toolbar: false,
      mention: mentionModule(debouncedMentionFunc),
    }),
    [],
  );

  return (
    <div
      id={`editorContainer - ${props.id}`}
      ref={containerRef}
      className={`u-flex u-flex--gap-half ${props.className}`}
    >
      <ReactQuill
        id={'editorContainer'}
        key={props.id}
        ref={quillRef}
        className={cx(styles.editor, { [styles.editorSmall]: size === 'small' })}
        onChangeSelection={selection => {
          if (selection) {
            setQuillCurrentSelection(selection.index);
          }
        }}
        onChange={debouncedChangeFunc}
        placeholder={props.editorPlaceholder}
        modules={{ ...modules }}
        formats={formats}
        preserveWhitespace
      />
      <div className={styles.messageFormButtons}>
        {props.onCancel && (
          <span className="u-half-spacing-right">
            <Button kind="tertiary" onClick={props.onCancel}>
              Cancel
            </Button>
          </span>
        )}
        <Button
          size={size}
          kind="primary"
          isLoading={isSaving}
          disabled={isEmpty}
          className={styles.submitButton}
          onClick={() => formik.handleSubmit()}
        >
          Send
        </Button>
      </div>
      <UserProfileDialog
        isOpen={!!userProfileIdToShow}
        cwUserId={userProfileIdToShow!}
        onClose={() => setUserProfileIdToShow(undefined)}
      />
      {companyProfileIdToShow && (
        <PublicProfileDialog companyId={companyProfileIdToShow} onClose={() => setCompanyProfileIdToShow(undefined)} />
      )}
    </div>
  );
}

Quill.register('modules/magicUrl', MagicUrl);
